import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useMode } from '@/providers/ModeProvider';
import { format } from 'date-fns';
import {
  HandCoins,
  Mail,
  Phone,
  Calendar,
  User,
  FileText,
  MessageSquare,
  Save,
  X,
  Search,
  Loader2,
  CheckCircle2,
  Building2,
  Folder,
  Activity,
  Clock,
  AlertCircle,
  ArrowDownToLine,
  ArrowRightLeft,
} from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { TransactionTable } from '@/components/transactions/transaction-table';
import { TransactionDetailSheet } from '@/components/transactions/transaction-detail-sheet';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useCustomerContactsList } from '@/hooks/useCustomerContacts';
import type { ContactUI } from '@/types/ui/customer-detail';
import type { Transaction, ColumnVisibility } from '@/types/transaction';
import { formatCurrency } from '@/lib/utils/formatters';
import { useCompany } from '@/contexts/CompanyContext';
import { CurrencyIcon } from '@/components/shared/currency-icon';
import { useCreatePromiseMutation, getConfirmationMethodId, useCreatePromiseCustomers, useCreatePromiseCollectionCases, type CreatePromiseFormCustomer, type CreatePromiseFormTransaction } from '@/hooks/useCreatePromiseData';
import { useCreatePromiseTransactionsPaginatedGraphQL, buildCreatePromiseTransactionExportFilter, CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP } from '@/hooks/useCreatePromiseTransactionsGraphQL';
import { useExportAll } from '@/hooks/useExportAll';
import { Forms } from '@/types/export';
import type { ColumnInput } from '@/types/export';
import { ExportProvider } from '@/providers/ExportProvider';
import { useNavigate } from 'react-router-dom';

interface CreatePromiseDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (data: PromiseFormData) => void;
  prePopulatedCustomer?: {
    customerId: string;
    customerName: string;
    accountNumber: string;
  };
  prePopulatedTransactions?: MockInvoice[];
  prePopulatedCaseId?: string;
  prePopulatedCaseNumber?: string;
  triggerSource?: 'case-details-button' | 'action-bar';
}

// Internal form state type
interface PromiseFormState {
  // Customer Information
  customerId: string;
  customerName: string;
  customerAccountNumber: string;
  contactId: string;
  contactName: string;
  customerEmail: string;
  customerPhone: string;
  
  // Promise Details
  promiseDate: Date | undefined;
  promisedAmount: string;
  confirmationMethod: string;
  caseId?: string;  // Optional link to collection case
  
  // Invoice Selection
  selectedInvoices: Set<string>;
  invoiceAllocations: Record<string, number>;
  
  // Notes
  notes: string;
  sendConfirmation: boolean;
}

// Submit payload type for onSave callback
export interface PromiseFormData {
  customer: {
    id: string;
    name: string;
    accountNumber: string;
    email: string;
    phone: string;
  };
  promiseDate: Date;
  promisedAmount: number;
  confirmationMethod: string;
  selectedInvoices: string[];
  invoiceAllocations: Record<string, number>;
  notes: string;
  sendConfirmation: boolean;
}

interface MockInvoice {
  id: string;
  headerReference: string;
  invoiceDate: Date;
  originalAmount: number;
  balanceRemaining: number;
  daysOverdue: number;
  status?: string | null;
}

// Mock customer data for demonstration
const mockCustomers = [
  {
    id: '1',
    name: 'Acme Corporation',
    accountNumber: 'ACC-001',
    email: 'accounts@acme.com',
    phone: '+44 20 7946 0301',
  },
  {
    id: '2',
    name: 'Global Industries Ltd',
    accountNumber: 'ACC-002',
    email: 'finance@globalind.com',
    phone: '+44 121 496 0304',
  },
  {
    id: '3',
    name: 'Tech Solutions Inc',
    accountNumber: 'ACC-003',
    email: 'billing@techsol.com',
    phone: '+44 161 850 0306',
  },
];

// Note: Transaction data now comes from GraphQL via useCreatePromiseTransactionsPaginatedGraphQL

const DEFAULT_CREATE_PROMISE_DRAWER_TX_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber', label: 'Reference Number', visible: true, order: 0, locked: true },
  { id: 'transactionDate', label: 'Transaction Date', visible: true, order: 1 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 2 },
  { id: 'transactionType', label: 'Type', visible: true, order: 3 },
  { id: 'amount', label: 'Gross', visible: true, order: 4 },
  { id: 'currency', label: 'Currency', visible: true, order: 5 },
  { id: 'outstandingAmount', label: 'Outstanding', visible: true, order: 6 },
  { id: 'promisedAmount', label: 'Promised Amount', visible: true, order: 7, locked: true },
  { id: 'status', label: 'Status', visible: true, order: 8 },
  { id: 'description', label: 'Description', visible: false, order: 9 },
  { id: 'accountCode', label: 'Account', visible: false, order: 10 },
  { id: 'netAmount', label: 'Net', visible: false, order: 11 },
  { id: 'vatAmount', label: 'Tax', visible: false, order: 12 },
  { id: 'subLedger', label: 'Sub Ledger', visible: false, order: 13 },
  { id: 'orderNumber', label: 'Order Number', visible: false, order: 14 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 15 },
  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 16 },
  { id: 'customerName', label: 'Customer Name', visible: false, order: 17 },
  { id: 'flags', label: 'Flags', visible: true, order: 18 },
  { id: 'onDispute', label: 'External Dispute', visible: false, order: 19 },
  { id: 'actions', label: 'Actions', visible: true, order: 20 },
];

function validateAllocationChange(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  availableInvoices: any[],
  invoiceId: string,
  amount: number,
): string | null {
  const invoice = availableInvoices.find(inv => inv.id === invoiceId);
  if (!invoice) return null;

  const outstanding = invoice.balanceRemaining;
  if (outstanding !== 0 && amount !== 0 && Math.sign(amount) !== Math.sign(outstanding)) {
    return `Amount sign must match the outstanding amount sign (${outstanding >= 0 ? 'positive' : 'negative'})`;
  }
  if (Math.abs(amount) > Math.abs(outstanding)) {
    return `Amount cannot exceed outstanding amount of ${formatCurrency(outstanding)}`;
  }
  return null;
}

export const CreatePromiseDrawer: React.FC<CreatePromiseDrawerProps> = ({ open, onOpenChange, onSave, prePopulatedCustomer, prePopulatedTransactions, prePopulatedCaseId, prePopulatedCaseNumber, triggerSource }) => {
  const { isDemo } = useMode();
  const { selectedCompany } = useCompany();
  const navigate = useNavigate();
  const createPromiseMutation = useCreatePromiseMutation();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCustomer, setSelectedCustomer] = useState<CreatePromiseFormCustomer | null>(null);
  const [selectedContact, setSelectedContact] = useState<ContactUI | null>(null);
  
  const [formData, setFormData] = useState<PromiseFormState>({
    customerId: '',
    customerName: '',
    customerAccountNumber: '',
    contactId: '',
    contactName: '',
    customerEmail: '',
    customerPhone: '',
    promiseDate: undefined,
    promisedAmount: '',
    confirmationMethod: '',
    selectedInvoices: new Set(),
    invoiceAllocations: {},
    notes: '',
    sendConfirmation: true,
  });

  // Wire up API-backed data for live mode
  const { customers: apiCustomers } = useCreatePromiseCustomers();
  const { cases: apiCases } = useCreatePromiseCollectionCases(selectedCustomer?.id);

  // Search, column config, sorting, pagination state for transactions table
  const [transactionSearchQuery, setTransactionSearchQuery] = useState('');
  const {
    columns: promiseTxCols,
    savePreferences: savePromiseTxPrefs,
    resetToDefaults: resetPromiseTxPrefs,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.CreatePromiseDrawerTransactions, DEFAULT_CREATE_PROMISE_DRAWER_TX_COLUMNS);
  const columnVisibility = useMemo(() => {
    const vis = {} as ColumnVisibility;
    promiseTxCols.forEach((col) => { vis[col.id as keyof ColumnVisibility] = col.visible; });
    return vis;
  }, [promiseTxCols]);

  const [sortBy, setSortBy] = useState<string>('transactionDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [txCurrentPage, setTxCurrentPage] = useState(1);
  const [txItemsPerPage, setTxItemsPerPage] = useState(25);

  // Transaction detail sheet state
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [transactionDrawerOpen, setTransactionDrawerOpen] = useState(false);

  // When triggered from action bar or case details, filter to only show the passed-in transactions
  const actionBarTransactionIds = useMemo(() => {
    if ((triggerSource === 'action-bar' || triggerSource === 'case-details-button') && prePopulatedTransactions && prePopulatedTransactions.length > 0) {
      return prePopulatedTransactions.map(t => Number(t.id));
    }
    return undefined;
  }, [triggerSource, prePopulatedTransactions]);

  // GraphQL-based transaction data (always used as the data source)
  const {
    transactions: graphqlTransactions,
    pagination: txPagination,
  } = useCreatePromiseTransactionsPaginatedGraphQL(
    formData.customerId || undefined,
    {
      page: txCurrentPage,
      pageSize: txItemsPerPage,
      search: transactionSearchQuery || undefined,
      sortBy,
      sortOrder,
      transactionIds: actionBarTransactionIds,
    },
  );

  // Map GraphQL Transaction[] to CreatePromiseFormTransaction[] for form compatibility
  const apiTransactions: CreatePromiseFormTransaction[] = useMemo(() =>
    graphqlTransactions.map((tx): CreatePromiseFormTransaction => {
      const today = new Date();
      const dueDate = tx.dueDate ? new Date(tx.dueDate) : new Date();
      const daysOverdue = Math.max(0, Math.ceil((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)));
      return {
        id: String(tx.id),
        headerReference: tx.referenceNumber ?? `TXN-${tx.id}`,
        invoiceDate: tx.transactionDate ? new Date(tx.transactionDate) : new Date(),
        originalAmount: tx.amount ?? 0,
        balanceRemaining: tx.displayOutstandingAmount ?? tx.outstandingAmount ?? 0,
        daysOverdue,
        dueDate: tx.dueDate ? new Date(tx.dueDate) : null,
        transactionType: tx.transactionType ?? null,
        outstandingAmount: tx.outstandingAmount ?? null,
        currency: tx.currency ?? null,
        description: tx.description ?? null,
        accountCode: tx.accountCode ?? null,
        netAmount: tx.netAmount ?? null,
        vatAmount: tx.vatAmount ?? null,
        subLedger: tx.subLedger ?? null,
        orderNumber: tx.orderNumber ?? null,
        createdBy: tx.createdBy ?? null,
        modifiedBy: tx.modifiedBy ?? null,
        onDispute: tx.onDispute ?? null,
        status: tx.status ?? null,
      };
    }),
  [graphqlTransactions]);

  const availableInvoices: CreatePromiseFormTransaction[] = apiTransactions;

  // Display transactions for the TransactionTable component
  const displayTransactions: Transaction[] = useMemo(() => {
    return graphqlTransactions;
  }, [graphqlTransactions]);

  // Client-side total using displayOutstandingAmount to account for foreign currency
  const displayTotalOutstanding = useMemo(() => {
    return graphqlTransactions.reduce(
      (sum, tx) => sum + (tx.displayOutstandingAmount ?? tx.outstandingAmount ?? 0),
      0,
    );
  }, [graphqlTransactions]);

  // Column configuration handler
  const handleColumnConfigApply = (columns: ColumnDefinition[]) => {
    savePromiseTxPrefs(columns);
  };

  // Server-side sort change handler
  const onSortChange = useCallback((newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setTxCurrentPage(1);
  }, []);

  // Handle transaction click - open detail sheet
  const handleTransactionClick = useCallback((transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setTransactionDrawerOpen(true);
  }, []);

  // Server-side XLSX export — build columns and filter
  const getExportFilter = useCallback((): Record<string, unknown> | undefined => {
    return buildCreatePromiseTransactionExportFilter(formData.customerId || undefined, transactionSearchQuery || undefined, actionBarTransactionIds);
  }, [formData.customerId, transactionSearchQuery, actionBarTransactionIds]);

  const getExportSummary = useCallback((): string | undefined => {
    return transactionSearchQuery || undefined;
  }, [transactionSearchQuery]);

  const getVisibleExportColumns = useCallback((): ColumnInput[] => {
    return promiseTxCols
      .filter((col) => col.visible && col.id in CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP)
      .map((col) => ({
        name: CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP[col.id],
        displayName: col.label,
      }));
  }, [promiseTxCols]);

  const getAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_CREATE_PROMISE_DRAWER_TX_COLUMNS
      .filter((col) => col.id in CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP)
      .map((col) => ({
        name: CREATE_PROMISE_TRANSACTION_EXPORT_COLUMN_MAP[col.id],
        displayName: col.label,
      }));
  }, []);

  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: Forms.CreatePromiseTransactions,
    getColumns: getVisibleExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `create-promise-drawer-transactions-${selectedCustomer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: Forms.CreatePromiseTransactions,
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `create-promise-drawer-transactions-all-columns-${selectedCustomer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const handleExportCurrentView = useCallback(() => {
    triggerExportCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerExportCurrentView]);

  const handleExportAllColumns = useCallback(() => {
    triggerExportAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerExportAllColumns]);

  // Fetch contacts for the selected customer via API (live) or mock (demo)
  const { data: availableContacts = [] } = useCustomerContactsList(selectedCustomer?.id ?? "");

  // Auto-select primary contact when contacts load
  useEffect(() => {
    if (availableContacts.length > 0 && !selectedContact) {
      const primaryContact = availableContacts.find(x => x.isPrimary);
      if (primaryContact) {
        setSelectedContact(primaryContact);
        setFormData(prev => ({
          ...prev,
          contactId: primaryContact.id,
          contactName: `${primaryContact.firstName} ${primaryContact.surname}`,
          customerEmail: primaryContact.email,
          customerPhone: primaryContact.phone,
        }));
      }
    }
    if (availableContacts.length === 0 && selectedContact) {
      setSelectedContact(null);
    }
  }, [availableContacts, selectedContact]);

  // Handle pre-populated customer when drawer opens
  useEffect(() => {
    if (open && prePopulatedCustomer) {
      // Find the customer in mockCustomers by ID or create a customer object
      const customer = isDemo ? mockCustomers.find(c => c.id === prePopulatedCustomer.customerId) : undefined;
      
      if (customer) {
        // Use existing customer from mock data
        setSelectedCustomer(customer);
        setFormData(prev => ({
          ...prev,
          customerId: customer.id,
          customerName: customer.name,
          customerAccountNumber: customer.accountNumber,
        }));
      } else {
        // Create a customer object from pre-populated data
        const newCustomer = {
          id: prePopulatedCustomer.customerId,
          name: prePopulatedCustomer.customerName,
          accountNumber: prePopulatedCustomer.accountNumber,
          email: '',
          phone: '',
        };
        setSelectedCustomer(newCustomer);
        setFormData(prev => ({
          ...prev,
          customerId: newCustomer.id,
          customerName: newCustomer.name,
          customerAccountNumber: newCustomer.accountNumber,
        }));
      }
    }
    
    // Reset auto-selection guard when drawer opens so the GraphQL-based effect can run
    if (open && (triggerSource === 'action-bar' || triggerSource === 'case-details-button')) {
      hasAutoSelectedRef.current = false;
    }

    // Preset caseId if provided
    if (open && prePopulatedCaseId) {
      setFormData(prev => ({
        ...prev,
        caseId: prePopulatedCaseId,
      }));
    }
  }, [open, prePopulatedCustomer, prePopulatedCaseId, triggerSource]);

  // Auto-select transactions from GraphQL data for both case-details-button and action-bar.
  // Uses displayOutstandingAmount (via apiTransactions.balanceRemaining) to handle foreign currency.
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (
      !open ||
      hasAutoSelectedRef.current ||
      apiTransactions.length === 0 ||
      (triggerSource !== 'action-bar' && triggerSource !== 'case-details-button')
    ) {
      return;
    }
    hasAutoSelectedRef.current = true;

    const selectedIds = new Set(apiTransactions.map(t => t.id));
    const allocations: Record<string, number> = {};
    for (const tx of apiTransactions) {
      allocations[tx.id] = tx.balanceRemaining;
    }
    const totalAllocated = Object.values(allocations).reduce((sum, amt) => sum + amt, 0);

    setFormData(prev => ({
      ...prev,
      selectedInvoices: selectedIds,
      invoiceAllocations: allocations,
      promisedAmount: totalAllocated > 0 ? totalAllocated.toFixed(2) : '',
    }));
  }, [open, triggerSource, apiTransactions]);

  // Reset form when drawer closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset form
      hasAutoSelectedRef.current = false;
      setActiveTab('overview');
      setSelectedCustomer(null);
      setSelectedContact(null);
      setTransactionSearchQuery('');
      setSortBy('transactionDate');
      setSortOrder('desc');
      setTxCurrentPage(1);
      setTxItemsPerPage(25);
      setSelectedTransaction(null);
      setTransactionDrawerOpen(false);
      setFormData({
        customerId: '',
        customerName: '',
        customerAccountNumber: '',
        contactId: '',
        contactName: '',
        customerEmail: '',
        customerPhone: '',
        promiseDate: undefined,
        promisedAmount: '',
        confirmationMethod: '',
        selectedInvoices: new Set(),
        invoiceAllocations: {},
        notes: '',
        sendConfirmation: true,
      });
    }
    onOpenChange(newOpen);
  };

  // Handle customer selection
  const handleSelectCustomer = (customerId: string) => {
    const customer = apiCustomers.find(c => c.id === customerId);
    if (customer) {
      setSelectedCustomer(customer);
      setFormData(prev => ({
        ...prev,
        customerId: customer.id,
        customerName: customer.name,
        customerAccountNumber: customer.accountNumber,
        // Reset contact fields - will be populated by useEffect
        contactId: '',
        contactName: '',
        customerEmail: '',
        customerPhone: '',
      }));
    }
  };

  // Handle contact selection
  const handleSelectContact = (contactId: string) => {
    const contact = availableContacts.find(x => x.id === contactId);
    if (contact) {
      setSelectedContact(contact);
      setFormData(prev => ({
        ...prev,
        contactId: contact.id,
        contactName: `${contact.firstName} ${contact.surname}`,
        customerEmail: contact.email,
        customerPhone: contact.phone,
      }));
    }
  };

  // Derive the promise currency from the currently selected invoices
  const promiseCurrency = useMemo(() => {
    for (const invoiceId of Array.from(formData.selectedInvoices)) {
      const invoice = availableInvoices.find(inv => inv.id === invoiceId);
      if (invoice?.currency) return invoice.currency;
    }
    return undefined;
  }, [formData.selectedInvoices, availableInvoices]);

  // Handle invoice selection
  const handleToggleInvoice = (invoiceId: string, checked: boolean) => {
    const newSelected = new Set(formData.selectedInvoices);
    const newAllocations = { ...formData.invoiceAllocations };
    
    if (checked) {
      const invoice = availableInvoices.find(inv => inv.id === invoiceId);
      if (invoice?.currency && promiseCurrency && invoice.currency !== promiseCurrency) {
        toast.error(`Cannot mix currencies. This promise uses ${promiseCurrency} but the selected transaction is in ${invoice.currency}.`);
        return;
      }
      newSelected.add(invoiceId);
      if (invoice) {
        newAllocations[invoiceId] = invoice.balanceRemaining;
      }
    } else {
      newSelected.delete(invoiceId);
      delete newAllocations[invoiceId];
    }
    
    // Calculate total allocated amount
    const totalAllocated = Object.values(newAllocations).reduce((sum, amount) => sum + amount, 0);
    
    setFormData({
      ...formData,
      selectedInvoices: newSelected,
      invoiceAllocations: newAllocations,
      promisedAmount: totalAllocated > 0 ? totalAllocated.toFixed(2) : '',
    });
  };

  // Handle allocation amount change with validation
  const handleAllocationChange = (invoiceId: string, amount: number) => {
    const error = validateAllocationChange(availableInvoices, invoiceId, amount);
    if (error) {
      toast.error(error);
      return;
    }

    const newAllocations = {
      ...formData.invoiceAllocations,
      [invoiceId]: amount,
    };
    
    // Calculate total allocated amount
    const totalAllocated = Object.values(newAllocations).reduce((sum, amt) => sum + amt, 0);
    
    setFormData({
      ...formData,
      invoiceAllocations: newAllocations,
      promisedAmount: totalAllocated > 0 ? totalAllocated.toFixed(2) : '',
    });
  };

  // Select all visible transactions and allocate their outstanding amounts (currency-aware)
  const handleSelectAllTransactions = useCallback(() => {
    const baseCurrency = promiseCurrency
      ?? availableInvoices.find(inv => inv.currency)?.currency;
    const newSelected = new Set(formData.selectedInvoices);
    const newAllocations = { ...formData.invoiceAllocations };
    let skipped = false;
    for (const tx of displayTransactions) {
      const txId = String(tx.id);
      if (baseCurrency && tx.currency && tx.currency !== baseCurrency) {
        skipped = true;
        continue;
      }
      newSelected.add(txId);
      if (!(txId in newAllocations)) {
        newAllocations[txId] = tx.displayOutstandingAmount ?? tx.outstandingAmount ?? 0;
      }
    }
    const total = Object.values(newAllocations).reduce((sum, amt) => sum + amt, 0);
    setFormData({ ...formData, selectedInvoices: newSelected, invoiceAllocations: newAllocations, promisedAmount: total > 0 ? total.toFixed(2) : '' });
    if (skipped) {
      toast.error(`Some transactions were skipped because they use a different currency than the already selected transactions (${baseCurrency}).`);
    }
  }, [displayTransactions, formData, promiseCurrency, availableInvoices]);

  // Deselect all transactions and clear allocations
  const handleDeselectAllTransactions = useCallback(() => {
    setFormData({ ...formData, selectedInvoices: new Set(), invoiceAllocations: {}, promisedAmount: '' });
  }, [formData]);

  // Map-based handler passed to TransactionTable's onSelectAll prop
  // Uses a lookup map instead of boolean branching to satisfy SonarQube S2301
  const selectAllActions = useMemo(() => ({
    true: handleSelectAllTransactions,
    false: handleDeselectAllTransactions,
  }), [handleSelectAllTransactions, handleDeselectAllTransactions]);

  const handleSelectAllToggle = useCallback(
    (checked: boolean) => selectAllActions[String(checked) as 'true' | 'false'](),
    [selectAllActions],
  );

  // Calculate total promised amount from allocations
  const calculateTotalFromAllocations = () => {
    return Object.values(formData.invoiceAllocations).reduce((sum, amount) => sum + amount, 0);
  };

  // Validation
  const validateForm = (): string | null => {
    if (!selectedCustomer) {
      return 'Please select a customer';
    }
    
    if (!selectedContact) {
      return 'Please select a contact';
    }
    
    if (!formData.promiseDate) {
      return 'Please select a promise date';
    }
    
    if (!formData.promisedAmount || parseFloat(formData.promisedAmount) <= 0) {
      return 'Please enter a valid promised amount';
    }
    
    if (!formData.confirmationMethod) {
      return 'Please select a confirmation method';
    }
    
    if (formData.selectedInvoices.size === 0) {
      return 'Please select at least one transaction';
    }

    // Currency consistency validation
    const selectedTxs = Array.from(formData.selectedInvoices)
      .map(id => availableInvoices.find(inv => inv.id === id))
      .filter((tx): tx is NonNullable<typeof tx> => Boolean(tx));
    const currencies = [...new Set(selectedTxs.map(tx => tx.currency).filter(Boolean))];
    if (currencies.length > 1) {
      return `All transactions must share the same currency. Found: ${currencies.join(', ')}`;
    }
    
    const totalAllocated = calculateTotalFromAllocations();
    const promisedAmount = parseFloat(formData.promisedAmount);
    
    if (Math.abs(totalAllocated - promisedAmount) > 0.01) {
      return `Invoice allocations (${totalAllocated.toFixed(2)}) must equal promised amount (${promisedAmount.toFixed(2)})`;
    }
    
    return null;
  };

  // Handle save
  const handleSave = async () => {
    const validationError = validateForm();
    
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      // Build linked invoices from selected invoices
      const linkedInvoices = Array.from(formData.selectedInvoices).map(invoiceId => {
        const invoice = availableInvoices.find(inv => inv.id === invoiceId);
        const txId = parseInt(invoiceId, 10);
        return {
          transactionId: isNaN(txId) ? 0 : txId,
          invoiceNumber: invoice?.headerReference ?? '',
          invoiceDate: invoice?.invoiceDate ? format(invoice.invoiceDate, 'yyyy-MM-dd') : undefined,
          originalAmount: invoice?.originalAmount ?? 0,
          promisedAmount: formData.invoiceAllocations[invoiceId] ?? 0,
        };
      });

      const response = await createPromiseMutation.mutateAsync({
        customerId: parseInt(selectedCustomer!.id, 10),
        caseId: formData.caseId ? parseInt(formData.caseId, 10) : undefined,
        contactId: formData.contactId ? parseInt(formData.contactId, 10) : undefined,
        promiseDate: format(formData.promiseDate!, 'yyyy-MM-dd'),
        promisedAmount: parseFloat(formData.promisedAmount),
        confirmationMethodId: getConfirmationMethodId(formData.confirmationMethod),
        assignedUserId: 0,
        notes: formData.notes || undefined,
        sendConfirmation: formData.sendConfirmation,
        linkedInvoices,
      });

      toast.success('Promise to Pay created', {
        description: (
          <span>
            Promise{' '}
            <a
              href={`/collections/promises/${response.promiseId}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/collections/promises/${response.promiseId}`);
              }}
              style={{ color: '#0d9488', textDecoration: 'underline', fontWeight: 500 }}
            >
              #{response.promiseId}
            </a>
            {' '}created successfully
          </span>
        ),
        duration: 8000,
      });

      if (onSave) {
        onSave({
          customer: {
            id: selectedCustomer!.id,
            name: selectedCustomer!.name,
            accountNumber: selectedCustomer!.accountNumber,
            email: selectedCustomer!.email,
            phone: selectedCustomer!.phone,
          },
          promiseDate: formData.promiseDate!,
          promisedAmount: parseFloat(formData.promisedAmount),
          confirmationMethod: formData.confirmationMethod,
          selectedInvoices: Array.from(formData.selectedInvoices),
          invoiceAllocations: formData.invoiceAllocations,
          notes: formData.notes,
          sendConfirmation: formData.sendConfirmation,
        });
      }

      handleOpenChange(false);
    } catch (error) {
      toast.error(`Failed to create promise: ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    handleOpenChange(false);
  };

  const totalAllocated = calculateTotalFromAllocations();

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="w-full sm:max-w-[1100px] p-0 flex flex-col" hideCloseButton>
      <ExportProvider>
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <SheetTitle className="flex items-center gap-2 text-2xl">
                <HandCoins className="h-6 w-6 text-primary" />
                Create New Promise
              </SheetTitle>
              <SheetDescription className="mt-1">
                Record a new payment commitment from a customer
              </SheetDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={createPromiseMutation.isPending}>
                {createPromiseMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {createPromiseMutation.isPending ? "Creating..." : "Create Promise"}
              </Button>
            </div>
          </div>

          {/* Progress Indicator */}
          {selectedCustomer && (
            <div className="mt-4">
              <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-lg border border-primary/20">
                <User className="h-4 w-4 text-primary" />
                <span className="text-sm text-muted-foreground">Customer:</span>
                <span className="font-medium text-primary">{selectedCustomer.name}</span>
                <span className="text-sm text-muted-foreground">({selectedCustomer.accountNumber})</span>
              </div>
            </div>
          )}
        </SheetHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6 pt-4 border-b shrink-0">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview">
                Overview
              </TabsTrigger>
              <TabsTrigger value="transactions" className="flex items-center gap-2">
                Transactions
                {formData.selectedInvoices.size > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5">
                    {formData.selectedInvoices.size}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="activity">
                Activity
              </TabsTrigger>
              <TabsTrigger value="audit">
                Audit Log
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Overview Tab */}
            <TabsContent value="overview" className="px-6 py-4 space-y-6">
              {/* Two-Column Layout: Customer Information (Left) and Promise Details (Right) */}
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Customer Information Panel - LEFT */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Customer Information
                    </CardTitle>
                    <CardDescription>Select the customer and contact making the promise</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Customer Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        Customer *
                      </Label>
                      {prePopulatedCustomer ? (
                        <div className="space-y-2">
                          <p className="text-sm font-semibold">{formData.customerName} ({formData.customerAccountNumber})</p>
                          <Badge variant="secondary" className="text-xs">Pre-selected</Badge>
                        </div>
                      ) : (
                        <Select
                          value={formData.customerId}
                          onValueChange={handleSelectCustomer}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select a customer" />
                          </SelectTrigger>
                          <SelectContent>
                            {apiCustomers.map((customer) => (
                              <SelectItem key={customer.id} value={customer.id}>
                                {customer.name} ({customer.accountNumber})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Contact Name Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Contact Name *
                      </Label>
                      <Select
                        value={formData.contactId}
                        onValueChange={handleSelectContact}
                        disabled={!selectedCustomer || availableContacts.length === 0}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder={
                            !selectedCustomer 
                              ? "Select a customer first" 
                              : availableContacts.length === 0 
                                ? "No contacts available" 
                                : "Select a contact"
                          } />
                        </SelectTrigger>
                        <SelectContent>
                          {availableContacts.map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.firstName} {contact.surname}
                              {contact.isPrimary && " (Primary)"}
                              {contact.jobTitle && ` - ${contact.jobTitle}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Email Address (Auto-populated) */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        Email Address
                      </Label>
                      <Input
                        value={formData.customerEmail}
                        readOnly
                        disabled
                        className="h-9 bg-muted"
                        placeholder="Select a contact to view email"
                      />
                    </div>

                    {/* Phone Number (Auto-populated) */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        Phone Number
                      </Label>
                      <Input
                        value={formData.customerPhone}
                        readOnly
                        disabled
                        className="h-9 bg-muted"
                        placeholder="Select a contact to view phone"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Promise Details Panel - RIGHT */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <HandCoins className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      Promise Details
                    </CardTitle>
                    <CardDescription>Enter the payment commitment details</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Two-column grid layout */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                      {/* Promise Date - Left column */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Promise Date *
                        </Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                'w-full justify-start text-left font-normal h-9 text-sm',
                                !formData.promiseDate && 'text-muted-foreground'
                              )}
                            >
                              <Calendar className="mr-2 h-3 w-3" />
                              {formData.promiseDate ? format(formData.promiseDate, 'dd/MM/yyyy') : 'Pick date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <CalendarComponent
                              mode="single"
                              selected={formData.promiseDate}
                              onSelect={(date) => setFormData({ ...formData, promiseDate: date })}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Promised Amount - Right column */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <CurrencyIcon currencyCode={promiseCurrency || selectedCompany?.currency} className="h-3 w-3" />
                          Promised Amount *
                        </Label>
                        <Input
                          type="text"
                          placeholder="0.00"
                          value={formData.promisedAmount ? formatCurrency(parseFloat(formData.promisedAmount), promiseCurrency || selectedCompany?.currency || 'GBP') : ''}
                          readOnly
                          disabled
                          className="h-9 bg-muted font-medium"
                          title="This amount is automatically calculated from selected transactions"
                        />
                        <p className="text-xs text-muted-foreground">
                          Auto-calculated from transactions
                        </p>
                      </div>

                      {/* Confirmation Method - Left column */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Confirmation Method *
                        </Label>
                        <Select
                          value={formData.confirmationMethod}
                          onValueChange={(value) => setFormData({ ...formData, confirmationMethod: value })}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select method" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Phone">Phone</SelectItem>
                            <SelectItem value="Email">Email</SelectItem>
                            <SelectItem value="Portal">Portal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Related Collection Case - Right column */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Folder className="h-3 w-3" />
                          Collection Case (Optional)
                        </Label>
                        {prePopulatedCaseId ? (
                          <div className="space-y-2">
                            <p className="text-sm font-semibold">{prePopulatedCaseNumber || prePopulatedCaseId}</p>
                            <Badge variant="secondary" className="text-xs">Pre-selected</Badge>
                          </div>
                        ) : (
                          <Select
                            value={formData.caseId || 'none'}
                            onValueChange={(value) => setFormData({ ...formData, caseId: value === 'none' ? undefined : value })}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="None" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              {apiCases
                                .filter(c => c.status === 'Active')
                                .map((caseItem) => (
                                  <SelectItem key={caseItem.id} value={caseItem.id}>
                                    {caseItem.caseNumber}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>

                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Summary Card - Full Width Below */}
              {selectedCustomer && formData.promiseDate && formData.promisedAmount && formData.confirmationMethod && (
                <Card className="border-primary/20 bg-primary/5">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5 text-primary" />
                      Promise Summary
                    </CardTitle>
                    <CardDescription>Review the promise details before saving</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Customer</p>
                        <p className="text-sm font-semibold">{selectedCustomer.name}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Account Number</p>
                        <p className="text-sm font-mono font-semibold">{selectedCustomer.accountNumber}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Promise Date</p>
                        <p className="text-sm font-semibold">{format(formData.promiseDate, 'MMM dd, yyyy')}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Promised Amount</p>
                        <p className="text-sm font-bold text-primary">
                          {formatCurrency(parseFloat(formData.promisedAmount), promiseCurrency || selectedCompany?.currency || 'GBP')}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Confirmation Method</p>
                        <Badge variant="outline">{formData.confirmationMethod}</Badge>
                      </div>
                      {formData.caseId && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Collection Case</p>
                          <p className="text-sm font-mono font-semibold">
                            {apiCases.find(c => c.id === formData.caseId)?.caseNumber}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Promise Notes Panel - Full Width at Bottom */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    Promise Notes
                  </CardTitle>
                  <CardDescription>Add any additional information about this promise</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Textarea
                    placeholder="Enter notes about the promise, customer conversation, or any special conditions..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={6}
                    className="resize-none"
                  />
                  <p className="text-xs text-muted-foreground">
                    These notes will be visible in the promise details and activity history
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions" className="px-6 py-4 space-y-6 min-w-0">
              <Card className="min-w-0 overflow-hidden">
                <CardHeader>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <ArrowRightLeft className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <CardTitle className="text-lg">Outstanding Transactions</CardTitle>
                      {selectedCustomer && (
                        <Badge variant="secondary" className="ml-2">
                          {txPagination.totalItems}
                        </Badge>
                      )}
                    </div>
                    {selectedCustomer && (
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="search"
                            placeholder="Quick search..."
                            value={transactionSearchQuery}
                            onChange={(e) => { setTransactionSearchQuery(e.target.value); setTxCurrentPage(1); }}
                            className="pl-8 h-9 w-[200px]"
                          />
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-9 w-9"
                              title="Export to Excel"
                            >
                              <ArrowDownToLine className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleExportCurrentView}>
                              Export Current View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleExportAllColumns}>
                              Export All Columns
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <ColumnConfigDialog
                          columns={promiseTxCols}
                          onApply={handleColumnConfigApply}
                          onReset={resetPromiseTxPrefs}
                        />
                      </div>
                    )}
                  </div>
                  {!selectedCustomer && (
                    <CardDescription>
                      Select a customer first to view transactions
                    </CardDescription>
                  )}
                </CardHeader>
                {selectedCustomer && (
                <CardContent>
                  <div className="overflow-x-auto">
                  <TransactionTable
                      transactions={displayTransactions}
                      columnVisibility={columnVisibility}
                      orderedColumns={promiseTxCols}
                      onViewTransaction={handleTransactionClick}
                      showCustomerColumn={false}
                      displayAmounts={true}
                      enableSelection={true}
                      selectedRows={new Set(Array.from(formData.selectedInvoices).map(x => Number(x)))}
                      onSelectRow={(id, checked) => handleToggleInvoice(String(id), checked)}
                      onSelectAll={handleSelectAllToggle}
                      currentPage={txCurrentPage}
                      pageSize={txItemsPerPage}
                      totalItems={txPagination.totalItems}
                      onPageChange={setTxCurrentPage}
                      onPageSizeChange={(size) => { setTxItemsPerPage(size); setTxCurrentPage(1); }}
                      onSortChange={onSortChange}
                      renderActions={(transaction) => (
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleTransactionClick(transaction); }}>
                          View
                        </Button>
                      )}
                      renderPromisedAmount={(transaction) => {
                        const txId = String(transaction.id);
                        const isSelected = formData.selectedInvoices.has(txId);
                        if (isSelected) {
                          const allocatedAmount = formData.invoiceAllocations[txId] || 0;
                          return (
                            <CurrencyInput
                              currencyCode={transaction.currency || selectedCompany?.currency || 'GBP'}
                              value={allocatedAmount}
                              onChange={(val) => handleAllocationChange(txId, val ?? 0)}
                              min={0}
                              max={transaction.displayOutstandingAmount ?? transaction.outstandingAmount ?? undefined}
                              sign="positive"
                              className="w-32 text-right h-8"
                            />
                          );
                        }
                        return <span className="text-muted-foreground">-</span>;
                      }}
                    />
                  </div>

                  <Separator className="my-4" />
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total Promised Amount:</span>
                      <span className="text-xl font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(totalAllocated, promiseCurrency || selectedCompany?.currency || 'GBP')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total Outstanding:</span>
                      <span className="text-xl font-bold text-red-600 dark:text-red-400">
                        {formatCurrency(displayTotalOutstanding, promiseCurrency || selectedCompany?.currency || 'GBP')}
                      </span>
                    </div>
                  </div>
                </CardContent>
                )}
              </Card>
            </TabsContent>

            {/* Activity Tab - Consolidated view of Activity, Notes, and Communications */}
            <TabsContent value="activity" className="px-6 py-4 space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <CardTitle className="text-lg">Activity Timeline</CardTitle>
                      <CardDescription>
                        Add notes and view activity history for this promise
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add New Note Section */}
                  <div className="space-y-3 p-4 border rounded-lg bg-purple-50/50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-900">
                    <Label htmlFor="promiseNotes" className="text-sm font-medium flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Promise Notes
                    </Label>
                    <Textarea
                      id="promiseNotes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Enter notes about the promise, customer conversation, or any special conditions..."
                      rows={6}
                      className="resize-none"
                    />
                    <p className="text-xs text-muted-foreground">
                      These notes will be visible in the promise details and activity history
                    </p>
                  </div>

                  {/* Note Preview */}
                  {formData.notes && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Note Preview
                        </Label>
                        <div className="bg-muted p-4 rounded-lg border">
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{formData.notes}</p>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Information Banner */}
                  <Separator />
                  <div className="flex items-start gap-3 p-4 border rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
                    <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Activity History
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        Once this promise is created, all activities, notes, and communications will be tracked and displayed here in a unified timeline. This includes emails sent, calls logged, status changes, and any notes added by team members.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Audit Log Tab */}
            <TabsContent value="audit" className="px-6 py-4 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Audit Log
                  </CardTitle>
                  <CardDescription>
                    Complete chronological history of all promise modifications and changes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <Clock className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-base font-medium mb-2">No Audit History Yet</p>
                    <p className="text-sm max-w-md mx-auto">
                      Once this promise is created, all modifications and changes will be tracked here. This includes status changes, amount updates, date modifications, and field edits.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </ExportProvider>
      </SheetContent>

      {/* Transaction Details Drawer */}
      <TransactionDetailSheet
        open={transactionDrawerOpen}
        onOpenChange={setTransactionDrawerOpen}
        transaction={selectedTransaction}
      />
    </Sheet>
  );
};
